import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  AlertTriangle, ChevronRight, X, Share2, Search, Globe, 
  ChevronDown, ExternalLink, Info, AlertCircle, CheckCircle,
  XCircle, Moon, Sun, Zap, Shield, Calendar, HelpCircle,
  Briefcase, Heart, Package, Smartphone, Car, Globe2
} from 'lucide-react';
import countriesData from './countries.json';
import About from './About';

// Load countries from JSON file  
const COUNTRIES = countriesData;

// Category icons mapping - handles both emoji and clean versions
const CATEGORY_ICONS = {
  '💊 Medications': Heart,
  '🚬 Substances': Package,
  '🔪 Items & Devices': Briefcase,
  '🚗 Activities': Car,
  '💻 Digital': Smartphone,
  '🚫 Definitely Illegal': Shield,
  'Medications': Heart,
  'Substances': Package,
  'Items & Devices': Briefcase,
  'Activities': Car,
  'Digital': Smartphone,
  'Definitely Illegal': Shield
};

// Clean category names for display (removes emojis)
const getCleanCategoryName = (category) => {
  return category.replace(/^[^\w\s]+\s*/, '');
};

// Continent mapping for better organization
const CONTINENT_MAPPING = {
  // North America
  'US': 'North America',
  'CA': 'North America', 
  'MX': 'North America',
  
  // Europe
  'UK': 'Europe',
  'DE': 'Europe',
  'FR': 'Europe',
  'IT': 'Europe',
  'ES': 'Europe',
  'NL': 'Europe',
  'IE': 'Europe',
  'BE': 'Europe',
  'CH': 'Europe',
  'AT': 'Europe',
  'CZ': 'Europe',
  'HU': 'Europe',
  'SE': 'Europe',
  'NO': 'Europe',
  'DK': 'Europe',
  'FI': 'Europe',
  'IS': 'Europe',
  'GR': 'Europe',
  'PT': 'Europe',
  'PL': 'Europe',
  'UA': 'Europe',
  'LT': 'Europe',
  'RS': 'Europe',
  'BG': 'Europe',
  
  // Asia
  'JP': 'Asia',
  'TH': 'Asia',
  'SG': 'Asia',
  'IN': 'Asia',
  'KR': 'Asia',
  'PH': 'Asia',
  'MY': 'Asia',
  'ID': 'Asia',
  'VN': 'Asia',
  'TR': 'Asia',
  'IL': 'Asia',
  
  // Middle East
  'AE': 'Middle East',
  'SA': 'Middle East',
  'QA': 'Middle East',
  'KW': 'Middle East',
  
  // Africa
  'ZA': 'Africa',
  'EG': 'Africa',
  'MA': 'Africa',
  
  // South America
  'BR': 'South America',
  'AR': 'South America',
  'CL': 'South America',
  'CO': 'South America',
  'PE': 'South America',
  
  // Oceania
  'AU': 'Oceania',
  'NZ': 'Oceania',
  
  // Other
  'RU': 'Europe/Asia'
};

// Severity color coding
const getSeverityColor = (status) => {
  if (status.includes('❌')) return 'red';
  if (status.includes('⚠️')) return 'yellow';
  if (status.includes('✅')) return 'green';
  return 'gray';
};

const getSeverityBgClass = (status) => {
  if (status.includes('❌')) return 'bg-red-50 border-red-200 hover:border-red-300';
  if (status.includes('⚠️')) return 'bg-yellow-50 border-yellow-200 hover:border-yellow-300';
  if (status.includes('✅')) return 'bg-green-50 border-green-200 hover:border-green-300';
  return 'bg-gray-50 border-gray-200 hover:border-gray-300';
};

// Virtual scrolling component
const VirtualList = ({ items, renderItem, itemHeight = 60, containerHeight = 600 }) => {
  const [scrollTop, setScrollTop] = useState(0);
  const scrollElementRef = useRef(null);

  const startIndex = Math.floor(scrollTop / itemHeight);
  const endIndex = Math.min(
    items.length - 1,
    Math.ceil((scrollTop + containerHeight) / itemHeight)
  );

  const visibleItems = items.slice(startIndex, endIndex + 1);
  const totalHeight = items.length * itemHeight;
  const offsetY = startIndex * itemHeight;

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  return (
    <div
      ref={scrollElementRef}
      className="overflow-auto"
      style={{ height: containerHeight }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div style={{ transform: `translateY(${offsetY}px)` }}>
          {visibleItems.map((item, index) => renderItem(item, startIndex + index))}
        </div>
      </div>
    </div>
  );
};

// Autocomplete component
const AutocompleteSearch = ({ value, onChange, suggestions, placeholder, onSuggestionClick }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  useEffect(() => {
    if (value && suggestions) {
      const filtered = suggestions.filter(item =>
        item.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [value, suggestions]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowSuggestions(filteredSuggestions.length > 0)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm transition-all focus:ring-2 focus:ring-blue-500"
      />
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => {
                if (onSuggestionClick) {
                  onSuggestionClick(suggestion);
                } else {
                  onChange(suggestion);
                }
                setShowSuggestions(false);
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const LegalApp = () => {
  const [country, setCountry] = useState(null);
  const [showList, setShowList] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [quickViewMode, setQuickViewMode] = useState(false);
  const [showExplanations, setShowExplanations] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cachedCountries, setCachedCountries] = useState({});
  const [showAbout, setShowAbout] = useState(false);
  const [vpnSectionExpanded, setVpnSectionExpanded] = useState(true);
  const [showFAQ, setShowFAQ] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  const [searchAnnouncement, setSearchAnnouncement] = useState('');
  const [legendVisible, setLegendVisible] = useState(false);
  const [vpnSectionDismissed, setVpnSectionDismissed] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [favoriteCountries, setFavoriteCountries] = useState([]);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  // Rotating placeholder examples
  const placeholderExamples = ['weed', 'knife', 'vaping', 'drone', 'medication'];

  // Rotate placeholder examples (only when search is empty)
  useEffect(() => {
    if (searchTerm === '') {
      const interval = setInterval(() => {
        setCurrentPlaceholder(prev => (prev + 1) % placeholderExamples.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [searchTerm, placeholderExamples.length]);

  // Dark mode effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Pull to refresh handler
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    // Simulate refresh - in real app, fetch updated data
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  }, []);

  // Cache management
  useEffect(() => {
    if (country && !cachedCountries[country]) {
      setCachedCountries(prev => ({
        ...prev,
        [country]: { 
          data: COUNTRIES[country], 
          timestamp: Date.now() 
        }
      }));
    }
  }, [country, cachedCountries]);

  // Get all search suggestions with category mapping
  const getAllSearchTerms = () => {
    const terms = new Set();
    const termToCategory = {};
    
    if (country && COUNTRIES[country]) {
      Object.entries(COUNTRIES[country].categories).forEach(([categoryName, items]) => {
        Object.keys(items).forEach(item => {
          const lowerItem = item.toLowerCase();
          terms.add(lowerItem);
          termToCategory[lowerItem] = categoryName;
        });
      });
    }
    
    return { terms: Array.from(terms), termToCategory };
  };

  // Handle autocomplete click with smart category expansion
  const handleAutocompleteClick = (selectedTerm) => {
    try {
      if (country && COUNTRIES[country]) {
        const { termToCategory } = getAllSearchTerms();
        if (termToCategory) {
          const categoryName = termToCategory[selectedTerm.toLowerCase()];
          
          if (categoryName) {
            // Clear search and expand the category containing this item
            setSearchTerm('');
            setExpandedCategories(new Set([categoryName]));
            
            // Track successful autocomplete usage
            if (window.gtag) {
              window.gtag('event', 'autocomplete_success', {
                event_category: 'search',
                event_label: selectedTerm,
                value: 1
              });
            }
            return;
          }
        }
      }
    } catch (error) {
      console.error('Autocomplete error:', error);
    }
    // Fallback to regular search
    setSearchTerm(selectedTerm);
  };

  // Auto-detect country from IP with security and error handling
  useEffect(() => {
    if (!country && !showList) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      fetch('https://ipapi.co/json/', {
        signal: controller.signal,
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'default'
      })
        .then(res => {
          clearTimeout(timeoutId);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (data && typeof data.country_code === 'string' && data.country_code.length === 2) {
            const countryCode = data.country_code.toUpperCase();
            if (COUNTRIES[countryCode]) {
              setCountry(countryCode);
              if (window.gtag) {
                window.gtag('event', 'geolocation_success', {
                  event_category: 'user_location',
                  event_label: countryCode
                });
              }
            } else {
              setCountry('US');
            }
          } else {
            throw new Error('Invalid response format');
          }
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          console.warn('Geolocation failed:', error.message);
          setCountry('US');
        });
        
      return () => {
        clearTimeout(timeoutId);
        controller.abort();
      };
    }
  }, [country, showList]);

  // Initialize AdSense
  useEffect(() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.log('AdSense error:', e);
    }
  }, [country]);

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Show notification with auto-dismiss - moved before shareIt
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Error handling - moved before shareIt
  const handleError = useCallback((error, userMessage = 'Something went wrong') => {
    console.error('App Error:', error);
    setError({ message: userMessage, details: error.message });
    setTimeout(() => setError(null), 5000);
  }, []);

  const shareIt = useCallback(async () => {
    try {
      const c = COUNTRIES[country];
      const text = `${c.flag} ${c.name}: ${c.alert}\n\nCheck what's legal → isitlegalhere.com`;
      
      if (window.gtag) {
        window.gtag('event', 'share', {
          event_category: 'engagement',
          event_label: c.name,
          value: 1
        });
      }
      
      if (navigator.share) {
        await navigator.share({ text });
        showNotification('Shared successfully!', 'success');
      } else {
        await navigator.clipboard.writeText(text);
        showNotification('Copied to clipboard!', 'success');
      }
    } catch (error) {
      handleError(error, 'Failed to share. Please try again.');
    }
  }, [country, showNotification, handleError]);

  // Filter categories based on search (memoized to prevent infinite loops)
  const filterCategories = useCallback((categories) => {
    if (!searchTerm) return categories;
    
    const filtered = {};
    Object.entries(categories).forEach(([catName, items]) => {
      const filteredItems = {};
      Object.entries(items).forEach(([item, status]) => {
        if (item.toLowerCase().includes(searchTerm.toLowerCase()) ||
            status.toLowerCase().includes(searchTerm.toLowerCase())) {
          filteredItems[item] = status;
        }
      });
      if (Object.keys(filteredItems).length > 0) {
        filtered[catName] = filteredItems;
      }
    });
    return filtered;
  }, [searchTerm]);

  // Debounced search announcement for accessibility
  useEffect(() => {
    if (searchTerm) {
      const timer = setTimeout(() => {
        const c = COUNTRIES[country];
        if (c) {
          const filtered = filterCategories(c.categories);
          const totalResults = Object.values(filtered).reduce((sum, items) => sum + Object.keys(items).length, 0);
          const categoryCount = Object.keys(filtered).length;
          setSearchAnnouncement(
            `Search for "${searchTerm}" found ${totalResults} items in ${categoryCount} categories`
          );
        }
      }, 500); // 500ms debounce
      return () => clearTimeout(timer);
    } else {
      setSearchAnnouncement('');
    }
  }, [searchTerm, country, filterCategories]);

  // Add to search history when user searches
  const addToSearchHistory = useCallback((term) => {
    if (term && term.length > 2) {
      setSearchHistory(prev => {
        const filtered = prev.filter(item => item !== term);
        const newHistory = [term, ...filtered].slice(0, 5); // Keep last 5 searches
        return newHistory;
      });
    }
  }, []);

  // Debounced search history update
  useEffect(() => {
    if (searchTerm && searchTerm.length > 2) {
      const timer = setTimeout(() => {
        addToSearchHistory(searchTerm);
      }, 1000); // Add to history after 1 second of no typing
      return () => clearTimeout(timer);
    }
  }, [searchTerm, addToSearchHistory]);

  // Toggle favorite country
  const toggleFavorite = useCallback((countryCode) => {
    setFavoriteCountries(prev => {
      if (prev.includes(countryCode)) {
        return prev.filter(code => code !== countryCode);
      } else {
        return [...prev, countryCode].slice(0, 10); // Limit to 10 favorites
      }
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ignore if user is typing in an input
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'c':
          event.preventDefault();
          setShowList(true);
          showNotification('Press ESC to close country selector', 'info');
          break;
        case 'd':
          event.preventDefault();
          setDarkMode(prev => !prev);
          showNotification(`${darkMode ? 'Light' : 'Dark'} mode enabled`, 'info');
          break;
        case 'q':
          event.preventDefault();
          setQuickViewMode(prev => !prev);
          showNotification(`Quick view ${quickViewMode ? 'disabled' : 'enabled'}`, 'info');
          break;
        case 's':
          event.preventDefault();
          shareIt();
          break;
        case '/':
          event.preventDefault();
          // Focus search input
          const searchInput = document.querySelector('input[placeholder*="Search"]');
          if (searchInput) {
            searchInput.focus();
            showNotification('Search focused - start typing', 'info');
          }
          break;
        case 'escape':
          event.preventDefault();
          if (showList) {
            setShowList(false);
          } else if (searchTerm) {
            setSearchTerm('');
          }
          break;
        case '?':
          event.preventDefault();
          showNotification('Shortcuts: C=Countries, D=Dark Mode, Q=Quick View, S=Share, /=Search, ?=Help', 'info');
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showList, searchTerm, darkMode, quickViewMode, shareIt, showNotification]);

  // Quick view filter - only show critical items
  const getCriticalItems = (categories) => {
    const critical = {};
    Object.entries(categories).forEach(([catName, items]) => {
      const criticalItems = {};
      Object.entries(items).forEach(([item, status]) => {
        if (status.includes('❌') || status.includes('DEATH') || status.includes('prison')) {
          criticalItems[item] = status;
        }
      });
      if (Object.keys(criticalItems).length > 0) {
        critical[catName] = criticalItems;
      }
    });
    return critical;
  };

  // Organize countries by continent
  const organizeCountriesByContinent = () => {
    const continents = {};
    
    Object.entries(COUNTRIES).forEach(([code, country]) => {
      const continent = CONTINENT_MAPPING[code] || 'Other';
      if (!continents[continent]) {
        continents[continent] = [];
      }
      continents[continent].push({ code, ...country });
    });
    
    Object.keys(continents).forEach(continent => {
      continents[continent].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    return continents;
  };

  // About page
  if (showAbout) {
    return <About onBack={() => setShowAbout(false)} />;
  }

  // Simple FAQ page
  if (showFAQ) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 max-w-screen-sm mx-auto">
        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b dark:border-gray-700 z-10 shadow-sm">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => setShowFAQ(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                ← Back
              </button>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">FAQ</h1>
              <div className="w-9"></div>
            </div>
          </div>
        </header>
        <div className="p-4 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-2">Is this legal advice?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">No, this is NOT legal advice. Always verify with official sources and consult qualified attorneys.</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-2">How accurate is this information?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">We source from official government websites, but laws change frequently. Always verify current requirements.</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-2">How often is data updated?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">We review quarterly with critical changes updated immediately. Last updated: January 2025.</p>
          </div>
        </div>
      </div>
    );
  }

  // Country selector with virtual scrolling
  if (showList) {
    const continentData = organizeCountriesByContinent();
    const continentOrder = ['North America', 'Europe', 'Asia', 'Middle East', 'Africa', 'South America', 'Oceania', 'Europe/Asia', 'Other'];
    
    const filteredContinentData = {};
    if (countrySearchTerm) {
      Object.entries(continentData).forEach(([continent, countries]) => {
        const filtered = countries.filter(country => 
          country.name.toLowerCase().includes(countrySearchTerm.toLowerCase())
        );
        if (filtered.length > 0) {
          filteredContinentData[continent] = filtered;
        }
      });
    } else {
      Object.assign(filteredContinentData, continentData);
    }

    // Flatten countries for virtual scrolling
    const allCountries = [];
    continentOrder.forEach(continent => {
      if (filteredContinentData[continent]) {
        allCountries.push({ type: 'header', continent });
        filteredContinentData[continent].forEach(country => {
          allCountries.push({ type: 'country', ...country, continent });
        });
      }
    });
    
    return (
      <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-white'} max-w-screen-sm mx-auto transition-colors`}>
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b dark:border-gray-700 px-4 py-3 z-10">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-bold text-lg dark:text-white">Select Country</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                {darkMode ? <Sun className="w-5 h-5 text-gray-400" /> : <Moon className="w-5 h-5 text-gray-600" />}
              </button>
              <button onClick={() => {
                setShowList(false);
                setCountrySearchTerm('');
              }} className="text-gray-500 dark:text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <AutocompleteSearch
            value={countrySearchTerm}
            onChange={setCountrySearchTerm}
            suggestions={Object.values(COUNTRIES).map(c => c.name)}
            placeholder="Search countries..."
          />
        </div>
        
        {/* Favorite Countries */}
        {favoriteCountries.length > 0 && !countrySearchTerm && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">★ Favorite Countries</div>
            <div className="space-y-1">
              {favoriteCountries.map(code => {
                const favCountry = COUNTRIES[code];
                if (!favCountry) return null;
                return (
                  <button
                    key={code}
                    onClick={() => {
                      setCountry(code);
                      setShowList(false);
                      setExpandedCategories(new Set());
                      setSearchTerm('');
                      setCountrySearchTerm('');
                      setAlertDismissed(false);
                    }}
                    className="w-full flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{favCountry.flag}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{favCountry.name}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        <div className="p-2">
          {countrySearchTerm && allCountries.filter(item => item.type === 'country').length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No countries found for "{countrySearchTerm}"
            </div>
          )}
          
          <VirtualList
            items={allCountries}
            containerHeight={window.innerHeight - 120}
            itemHeight={60}
            renderItem={(item, index) => {
              if (item.type === 'header') {
                return (
                  <div key={`header-${item.continent}`} className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      {item.continent}
                    </h2>
                  </div>
                );
              }
              
              return (
                <button
                  key={item.code}
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'select_country', {
                        event_category: 'navigation',
                        event_label: item.name,
                        value: 1
                      });
                    }
                    setCountry(item.code);
                    setShowList(false);
                    setExpandedCategories(new Set());
                    setSearchTerm('');
                    setCountrySearchTerm('');
                    setAlertDismissed(false);
                  }}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-700 border-b border-gray-100 dark:border-gray-800 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{item.flag}</span>
                    <span className="font-medium text-left dark:text-white">{item.name}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              );
            }}
          />
        </div>
      </div>
    );
  }

  if (!country) {
    return (
      <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-white'} flex items-center justify-center p-4`}>
        <div className="text-center">
          <Globe className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="dark:text-white">Detecting location...</p>
        </div>
      </div>
    );
  }

  const c = COUNTRIES[country];
  const filteredCategories = filterCategories(c.categories);
  const displayCategories = quickViewMode ? getCriticalItems(filteredCategories) : filteredCategories;

  return (
    <div 
      className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'} max-w-screen-sm mx-auto transition-colors`}
      onTouchStart={(e) => {
        const touchY = e.touches[0].clientY;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        if (scrollTop === 0 && touchY > 10) {
          // Enable pull to refresh
          window.addEventListener('touchend', () => {
            if (window.pageYOffset < -50) {
              handleRefresh();
            }
          }, { once: true });
        }
      }}
    >
      {refreshing && (
        <div className="fixed top-0 left-0 right-0 bg-blue-500 text-white p-2 text-center z-50">
          Refreshing...
        </div>
      )}

      {/* Screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {searchAnnouncement}
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 left-4 right-4 z-50 mx-auto max-w-sm">
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 shadow-lg">
            <div className="flex gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900 dark:text-red-200">{error.message}</p>
                {error.details && (
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">{error.details}</p>
                )}
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className="fixed top-4 left-4 right-4 z-50 mx-auto max-w-sm">
          <div className={`${
            notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800' :
            notification.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800' :
            'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
          } border rounded-lg p-3 shadow-lg animate-fadeIn`}>
            <div className="flex gap-2 items-center">
              {notification.type === 'success' && <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />}
              {notification.type === 'warning' && <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />}
              {notification.type === 'info' && <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-900 dark:text-green-200' :
                notification.type === 'warning' ? 'text-yellow-900 dark:text-yellow-200' :
                'text-blue-900 dark:text-blue-200'
              }`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Site Navigation */}
      <nav className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <span className="font-bold text-gray-900 dark:text-white">⚖️ IsItLegalHere</span>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAbout(true)}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  About
                </button>
                <button
                  onClick={() => setShowFAQ(true)}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  FAQ
                </button>
                <button
                  onClick={() => showNotification('Keyboard Shortcuts: C=Countries, D=Dark Mode, Q=Quick View, S=Share, /=Search, ?=Help', 'info')}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-xs"
                  title="Keyboard shortcuts (or press ?)"
                >
                  ⌨️
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-500">
              Travel Law Guide • 100+ Countries
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="sticky top-0 bg-white dark:bg-gray-900 border-b dark:border-gray-700 z-10 shadow-sm transition-all" role="banner">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={() => setShowList(true)} 
              className="flex items-center gap-2 group"
              aria-label={`Current country: ${c.name}. Click to change country`}
              title={`Current country: ${c.name}. Click to change country`}
            >
              <span className="text-2xl group-hover:scale-110 transition-transform" role="img" aria-label={`${c.name} flag`}>
                {c.flag}
              </span>
              <div>
                <div className="font-bold text-gray-900 dark:text-white">{c.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Tap to change</div>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleFavorite(country)}
                className={`p-3 rounded-lg transition-all min-w-[44px] min-h-[44px] flex items-center justify-center ${favoriteCountries.includes(country) ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
                aria-label={favoriteCountries.includes(country) ? 'Remove from favorites' : 'Add to favorites'}
                title={favoriteCountries.includes(country) ? 'Remove from favorites' : 'Add to favorites'}
              >
                {favoriteCountries.includes(country) ? '★' : '☆'}
              </button>
              <button
                onClick={() => setQuickViewMode(!quickViewMode)}
                className={`p-3 rounded-lg transition-all min-w-[44px] min-h-[44px] flex items-center justify-center ${quickViewMode ? 'bg-red-100 dark:bg-red-900' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                aria-label="Quick view: Show only illegal items"
                title="Quick view: Show only illegal items"
              >
                <Zap className={`w-5 h-5 ${quickViewMode ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'}`} />
              </button>
              <button
                onClick={() => setShowExplanations(!showExplanations)}
                className={`p-3 rounded-lg transition-all min-w-[44px] min-h-[44px] flex items-center justify-center ${showExplanations ? 'bg-blue-100 dark:bg-blue-900' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                aria-label="Toggle detailed explanations"
                title="Toggle detailed explanations"
              >
                <Info className={`w-5 h-5 ${showExplanations ? 'text-blue-600' : 'text-gray-600 dark:text-gray-400'}`} />
              </button>
              <button
                onClick={() => setShowAbout(true)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="About this app"
                title="About this app"
              >
                <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Toggle dark mode"
                title="Toggle dark mode"
              >
                {darkMode ? <Sun className="w-5 h-5 text-gray-400" /> : <Moon className="w-5 h-5 text-gray-600" />}
              </button>
              <button 
                onClick={shareIt} 
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                aria-label={`Share legal information for ${c.name}`}
                title={`Share legal information for ${c.name}`}
              >
                <Share2 className="w-5 h-5 text-gray-600 dark:text-gray-400" aria-hidden="true" />
              </button>
            </div>
          </div>
          
          <AutocompleteSearch
            value={searchTerm}
            onChange={setSearchTerm}
            suggestions={country && COUNTRIES[country] ? getAllSearchTerms().terms : []}
            placeholder={`Search (e.g., '${placeholderExamples[currentPlaceholder]}')`}
            onSuggestionClick={handleAutocompleteClick}
          />
        </div>
      </header>

      {/* Quick View Mode Indicator */}
      {quickViewMode && (
        <div className="mx-4 mt-4 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
          <div className="flex gap-2 items-center">
            <Zap className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0" />
            <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
              Quick View: Showing only critical illegal items
            </p>
          </div>
        </div>
      )}

      {/* Color Coding Legend */}
      <div className="mx-4 mt-4">
        <button
          onClick={() => setLegendVisible(!legendVisible)}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          aria-label={`${legendVisible ? 'Hide' : 'Show'} color coding guide`}
          title={`${legendVisible ? 'Hide' : 'Show'} color coding guide`}
        >
          <HelpCircle className="w-4 h-4" />
          <span>Color Guide</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${legendVisible ? 'rotate-180' : ''}`} />
        </button>
        
        {legendVisible && (
          <div className="mt-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">Illegal</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">Restrictions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">Legal</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">Prescription</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Searches */}
      {searchHistory.length > 0 && !searchTerm && (
        <div className="mx-4 mt-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Recent searches:</div>
          <div className="flex flex-wrap gap-2">
            {searchHistory.map((term, index) => (
              <button
                key={`${term}-${index}`}
                onClick={() => setSearchTerm(term)}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {term}
              </button>
            ))}
            <button
              onClick={() => setSearchHistory([])}
              className="px-2 py-1 text-gray-400 dark:text-gray-500 text-xs hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
              title="Clear search history"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Explanations Mode Indicator */}
      {showExplanations && (
        <div className="mx-4 mt-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex gap-2 items-center">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
              Explanations Mode: Showing detailed context and sources for each law
            </p>
          </div>
        </div>
      )}

      {/* Color Coding Legend */}
      <div className="mx-4 mt-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Color Guide</h3>
        </div>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-gray-600 dark:text-gray-400">Illegal</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-600 dark:text-gray-400">Restricted</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-gray-600 dark:text-gray-400">Legal</span>
          </div>
        </div>
      </div>

      {/* Alert */}
      {!alertDismissed && (
        <div className="mx-4 mt-4 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 border-2 border-red-200 dark:border-red-800 rounded-lg p-3 shadow-sm">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-900 dark:text-red-200 font-medium">{c.alert}</p>
              {showExplanations && c.culturalContext && (
                <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                  <strong>Context:</strong> {c.culturalContext}
                </p>
              )}
            </div>
            <div className="flex items-start gap-1">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${c.name} Alert: ${c.alert}`);
                }}
                className="p-1 hover:bg-red-200 dark:hover:bg-red-800 rounded transition-colors"
                aria-label="Share this alert"
                title="Copy alert to clipboard"
              >
                <Share2 className="w-4 h-4 text-red-600 dark:text-red-400" />
              </button>
              <button
                onClick={() => setAlertDismissed(true)}
                className="p-1 hover:bg-red-200 dark:hover:bg-red-800 rounded transition-colors"
                aria-label="Dismiss alert"
                title="Dismiss this alert"
              >
                <X className="w-4 h-4 text-red-600 dark:text-red-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Common Mistakes Section */}
      {showExplanations && c.commonMistakes && (
        <div className="mx-4 mt-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">Common Mistakes:</p>
              <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
                {c.commonMistakes.map((mistake, idx) => (
                  <li key={idx}>• {mistake}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Seasonal Restrictions */}
      {c.seasonalRestrictions && (
        <div className="mx-4 mt-4 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
          <div className="flex gap-3">
            <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-purple-900 dark:text-purple-200 mb-1">Seasonal Notes:</p>
              <p className="text-xs text-purple-800 dark:text-purple-300">{c.seasonalRestrictions}</p>
            </div>
          </div>
        </div>
      )}

      {/* Categories with enhanced styling */}
      <div className="p-4">
        {Object.keys(displayCategories).length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {quickViewMode ? "No critical illegal items found" : `No results for "${searchTerm}"`}
          </div>
        ) : (
          Object.entries(displayCategories).map(([category, items]) => {
            const Icon = CATEGORY_ICONS[category] || Globe2;
            const cleanName = getCleanCategoryName(category);
            const hasIllegalItems = Object.values(items).some(status => status.includes('❌'));
            const hasWarningItems = Object.values(items).some(status => status.includes('⚠️'));
            
            return (
              <div key={category} className="mb-5">
                <button
                  onClick={() => toggleCategory(category)}
                  className={`w-full bg-gradient-to-r ${
                    hasIllegalItems ? 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200 dark:border-red-800' :
                    hasWarningItems ? 'from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border-yellow-200 dark:border-yellow-800' :
                    'from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-gray-200 dark:border-gray-600'
                  } rounded-lg border p-3 flex items-center justify-between hover:shadow-md transition-all`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <span className="font-semibold text-gray-900 dark:text-white">{cleanName}</span>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${
                      expandedCategories.has(category) ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {expandedCategories.has(category) && (
                  <div className={`mt-2 bg-white dark:bg-gray-800 rounded-lg border ${
                    hasIllegalItems ? 'border-red-200 dark:border-red-800' :
                    hasWarningItems ? 'border-yellow-200 dark:border-yellow-800' :
                    'border-gray-200 dark:border-gray-700'
                  } p-3 shadow-inner transition-all animate-fadeIn`}>
                    {Object.entries(items).map(([item, status]) => {
                      const severity = getSeverityColor(status);
                      const StatusIcon = severity === 'red' ? XCircle : 
                                        severity === 'yellow' ? AlertCircle : 
                                        severity === 'green' ? CheckCircle : Globe2;
                      
                      return (
                        <div key={item} className={`flex items-start justify-between py-3 border-b last:border-0 dark:border-gray-700 ${getSeverityBgClass(status)} px-3 rounded-md mb-2`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <StatusIcon className={`w-4 h-4 flex-shrink-0 ${
                                severity === 'red' ? 'text-red-500' :
                                severity === 'yellow' ? 'text-yellow-600' :
                                severity === 'green' ? 'text-green-500' : 'text-gray-400'
                              }`} />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
                            </div>
                            {showExplanations && c.explanations && c.explanations[item] && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 ml-6">
                                {c.explanations[item]}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-3">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const shareText = `${c.name} - ${item}: ${status}\n\nSource: isitlegalhere.com`;
                                  if (navigator.share) {
                                    await navigator.share({ text: shareText });
                                    showNotification('Shared successfully!', 'success');
                                  } else {
                                    await navigator.clipboard.writeText(shareText);
                                    showNotification('Copied to clipboard!', 'success');
                                  }
                                } catch (error) {
                                  handleError(error, 'Failed to share. Please try again.');
                                }
                              }}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors opacity-60 hover:opacity-100"
                              aria-label={`Share ${item} information`}
                              title={`Share ${item} information`}
                            >
                              <Share2 className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                            </button>
                            <span className="text-sm font-medium flex-shrink-0 dark:text-white">{status}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Top Banner Ad */}
      <div className="mx-4 mt-4 text-center">
        <ins className="adsbygoogle"
             style={{display: 'block'}}
             data-ad-client="ca-pub-3470675806627135"
             data-ad-slot="auto"
             data-ad-format="auto"
             data-full-width-responsive="true"></ins>
      </div>

      {/* VPN Recommendation - Collapsible and Dismissible */}
      {!vpnSectionDismissed && (
        <div className="mt-6 mx-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start">
            <button
              onClick={() => setVpnSectionExpanded(!vpnSectionExpanded)}
              className="flex-1 p-4 flex items-center justify-between hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors rounded-l-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200">Stay Protected Online</h3>
                  <p className="text-xs text-blue-700 dark:text-blue-400">VPN recommendations for travelers</p>
                </div>
              </div>
              <ChevronDown 
                className={`w-5 h-5 text-blue-600 dark:text-blue-400 transition-transform ${
                  vpnSectionExpanded ? 'rotate-180' : ''
                }`} 
              />
            </button>
            <button
              onClick={() => setVpnSectionDismissed(true)}
              className="p-4 hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors rounded-r-lg"
              aria-label="Dismiss VPN recommendations"
              title="Dismiss VPN recommendations"
            >
              <X className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </button>
          </div>
        
        {vpnSectionExpanded && (
          <div className="px-4 pb-4">
            <p className="text-xs text-blue-800 dark:text-blue-300 mb-3">
              Traveling abroad? Protect your data and access home content with a reliable VPN.
            </p>
            <div className="space-y-2">
              <a 
                href="https://get.surfshark.net/aff_c?offer_id=926&aff_id=41446"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-2 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'vpn_click', {
                      event_category: 'affiliate',
                      event_label: 'surfshark',
                      value: 1
                    });
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-blue-900 dark:text-blue-200">Surfshark VPN</div>
                    <div className="text-xs text-blue-700 dark:text-blue-400">Unlimited devices • Best value for travelers</div>
                  </div>
                  <div className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">Special Deal</div>
                </div>
              </a>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Bottom Banner Ad */}
      <div className="mx-4 mt-6 text-center">
        <ins className="adsbygoogle"
             style={{display: 'block'}}
             data-ad-client="ca-pub-3470675806627135"
             data-ad-slot="auto"
             data-ad-format="auto"
             data-full-width-responsive="true"></ins>
      </div>

      {/* Legal Disclaimer */}
      <div className="mt-8 mx-4 mb-4 p-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-bold text-red-900 dark:text-red-200 mb-1">⚖️ CRITICAL LEGAL DISCLAIMER</h3>
            <div className="text-xs text-red-800 dark:text-red-300 space-y-1">
              <p><strong>THIS IS NOT LEGAL ADVICE.</strong> This information is for general guidance only.</p>
              <p><strong>Laws change frequently.</strong> Information may be outdated or incomplete.</p>
              <p><strong>Always verify</strong> current laws with official government sources before traveling.</p>
              <p><strong>We are not responsible</strong> for legal consequences of your actions.</p>
              <p><strong>Consult a lawyer</strong> for specific legal advice about your situation.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sources & Methodology */}
      <div className="mt-4 mx-4 mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">📚 Data Sources & Methodology</h3>
        <div className="text-xs text-blue-800 dark:text-blue-300 space-y-2">
          <p><strong>Sources:</strong> Government websites, embassy guidelines, official legal databases, travel advisories.</p>
          <p><strong>Updated:</strong> January 2025 (laws may have changed since)</p>
          <p><strong>Coverage:</strong> 100+ countries, focus on common traveler violations</p>
          <p><strong>Limitations:</strong> Not comprehensive, regional variations may exist</p>
        </div>
      </div>

      {/* Official Sources */}
      <div className="mt-4 mx-4 mb-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <div className="border-t border-gray-300 dark:border-gray-600 pt-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">Official Sources:</p>
          <div className="space-y-1">
            {c.sources.map((source, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <ExternalLink className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-blue-600 dark:text-blue-400">{source.name} ({source.url})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-8 border-t border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
        <div className="px-4 py-6">
          <div className="text-center space-y-4">
            {/* Legal Links */}
            <div className="flex justify-center gap-6 text-sm">
              <button
                onClick={() => alert('Privacy Policy: This site uses minimal data collection. We do not store personal information. Contact us for details.')}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => alert('Terms of Service: This information is provided for educational purposes only. Not legal advice. Use at your own risk.')}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Terms of Service
              </button>
              <button
                onClick={() => setShowAbout(true)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                About the Project
              </button>
            </div>
            
            {/* Site Info */}
            <div className="text-xs text-gray-500 dark:text-gray-500 space-y-1">
              <p><strong>IsItLegalHere.com</strong> • Travel Law Guide for 100+ Countries</p>
              <p>Created by experienced travelers • Last updated: January 2025</p>
              <p>Data sources verified quarterly • For informational purposes only</p>
            </div>
            
            {/* Disclaimer */}
            <div className="text-xs text-gray-500 dark:text-gray-500 max-w-md mx-auto">
              <p>⚖️ <strong>Not legal advice.</strong> Laws change frequently. Always verify current requirements with official government sources before traveling.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalApp;